<template>
  <!-- HERO SECTION -->
  <Section fullwidth no-padding background="blue">
    <PageHero
      :background="heroBlindMap ? undefined : heroBackground"
      :mobileBackground="heroBlindMap ? undefined : heroMobileBackground"
      :breadcrumbs="breadcrumbs"
      :compact="hero_settings?.hero_compact"
    >
      <template v-if="heroBlindMap" #background>
        <BlindMap :locations="mapLocationsToBlindMap(heroBlindMap.locations)" />
      </template>
      <template #pretitle>{{ hero_settings?.hero_pre_title }}</template>
      <template #title>{{ hero_settings?.hero_title || title || name }}</template>

      <template v-if="heroOpeningHours?.hours?.length" #widget>
        <OpeningHours :data="heroOpeningHours?.hours" :address="heroAddress?.address" :map-link="heroAddress?.link" />
      </template>

      <template v-if="heroButtons?.length" #buttons>
        <CTA v-for="(button, i) in heroButtons"
          :text="button.hero_button_text"
          :link="button.hero_button_link"
          :color="!i ? button.hero_button_color || 'red' : 'white'"
          :outline="!!i" 
        />
      </template>
      <template v-else-if="pageableType === 'Career'" #buttons>
        <CTA v-if="(pageModel as any)?.pdf" :text="$t('career.download')" :link="(pageModel as any).pdf" icon="download" outline external />
      </template>

      <template v-if="heroNotice?.notice_text" #footer>
        <Note :text="heroNotice.notice_text" :color="heroNotice.notice_color" />
      </template>
    </PageHero>
  </Section>

  <!-- ANCHORS -->
  <AnchorNavigation :anchors="pageAnchors" v-if="pageAnchors?.length" />

  <!-- SPECIAL PAGES CONTENT -->
  <JobDetail v-if="pageableType === 'Career'" :title="title" :content="content" :form="form" :blocks="dynamic_content" />

  <!-- DYNAMIC BLOCKS -->
  <DynamicContentResolver v-if="dynamic_content?.length && !['Career'].includes(pageableType)"
    :blocks="(dynamic_content as DynamicContentBlocksFragment[])"
    :page-model="pageModel"
  />
</template>

<script lang="ts" setup>
import type { DynamicContentBlocksFragment } from '#gql'
import { mapLocationsToBlindMap } from '~/lib/data-mappings'
import { getBreadcrumbs, getResponsiveImage } from '~/lib/helpers'
import { throwFetchError, throwNotFound } from '~/lib/errors'
import { openJobModal } from '~/lib/modals' // do not remove

const route = useRoute()

const { locale } = useI18n()
useGqlHeaders({ 'Content-Language': locale.value })

// Parse route
const path = Array.isArray(route.params.slug)
  ? route.params.slug.filter(it => it !== '').join('/')
  : route.params.slug

// Fetch page data
const { data } = await useAsyncGql('GetPageable', { slug: path })

// Handle errors
if (!data.value) throwFetchError()
else if (data.value.pageableBySlug === null) throwNotFound()

route.meta.id = data.value.pageableBySlug?.id

// Redirect if page found, but slug does not match full path
if ( ![ path, `${locale.value}/${path}` ].includes(data.value.pageableBySlug?.full_url as string) ) {
  console.log('Got path mismatch, redirecting to', data.value.pageableBySlug?.full_url);
  await navigateTo('/' + data.value.pageableBySlug?.full_url, { redirectCode: 301, replace: true })
}

// Extract page data
const pageModel = data.value?.pageableBySlug?.model
const { title, name, dynamic_content, hero_settings, hero, image, content, form, __typename: pageableType } = pageModel || {} as any

const heroButtons = hero_settings?.hero_buttons?.[0]?.hero_button_text ? hero_settings?.hero_buttons : []
const heroOpeningHours = hero_settings?.extras.find(it => it.__typename === 'ExtraOpeningHours')
const heroAddress = hero_settings?.extras.find(it => it.__typename === 'ExtraAddress')
const heroBlindMap = hero_settings?.extras.find(it => it.__typename === 'ExtraBlindMap')
const heroNotice = hero_settings?.extras.find(it => it.__typename === 'ExtraNotice')
const heroImage = hero_settings?.image?.[0] || hero?.[0] || image
const heroBackground = hero_settings?.video?.[0] || heroImage
const heroMobileBackground = hero_settings?.video_mobile?.[0] || heroImage

const breadcrumbs = getBreadcrumbs(data.value.pageableBySlug)

// Get anchors from blocks for anchor navigation
const pageAnchors = dynamic_content?.reduce((anchors, block) => {
  if (!!block.anchor) anchors.push(block.anchor)
  return anchors
}, [])

useHead({
  title,
  link: heroImage ? [
    { rel: 'preload', href: getResponsiveImage(heroImage, '3840'), as: 'image'}
  ] : undefined
})

definePageMeta({
  middleware: [
    (to, from) => {
      // Prevent navigation to .js files caused by service workers
      if (to.path.endsWith('.js')) return abortNavigation()
    }
  ],
})
</script>